"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { AutoRetry } from "@/components/error/AutoRetry";

/**
 * Root-level Next.js error boundary.
 * Catches unhandled errors at the app level with a user-friendly UI.
 * Each route segment can have its own error.tsx for more granular error containment.
 */
export default function RootError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    useEffect(() => {
        console.error("[RootError] Unhandled error:", error);

        // Report to PostHog Error Tracking so the error appears in the dashboard
        posthog.captureException(error, {
            source: "next_error_boundary",
            digest: error.digest,
            route: typeof window !== "undefined" ? window.location.pathname : undefined,
        });
    }, [error]);

    const isConvexFunctionError =
        error.message?.toLowerCase().includes("could not find") ||
        error.message?.toLowerCase().includes("function signature");

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                {/* Icon */}
                <div className="relative mb-6 mx-auto w-20 h-20">
                    <div className="absolute inset-0 bg-orange-100 rounded-full animate-pulse opacity-60" />
                    <div className="relative w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                        <AlertTriangle size={36} className="text-white" />
                    </div>
                </div>

                {/* Message */}
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {isConvexFunctionError ? "Update in Progress" : "Something went wrong"}
                </h1>
                <p className="text-gray-500 text-sm mb-8 px-2">
                    {isConvexFunctionError
                        ? "The app is being updated. Please try again in a moment."
                        : "An unexpected error occurred. Please try again or contact support."}
                </p>

                {/* Actions */}
                <div className="space-y-3">
                    <AutoRetry duration={5} onRetry={() => window.location.reload()} theme="light" />

                    <button
                        onClick={() => window.location.reload()}
                        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                    >
                        <RefreshCw size={18} />
                        <span>Try Again</span>
                    </button>

                    <button
                        onClick={() => (window.location.href = "/")}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3.5 rounded-xl font-semibold active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                    >
                        <Home size={18} />
                        <span>Go Home</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
